import type { AirportRegion } from "@b2bportal/air-shopping-api";
import { logNotFoundTrackingProperties } from "@b2bportal/core-tracking";
import { SortOptionEnum, TripCategory } from "@b2bportal/core-types";
import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {
  getAirports,
  getFlights,
  getSearchFormValues,
  getSortOption,
} from "../../store";

type FlightShopSearchFormTrackingProperties = {
  advance: number;
  advanced: number;
  departure_date: string;
  destination: string;
  destination_country_code: string;
  los: number;
  origin: string;
  origin_country_code: string;
  return_date?: string;
  trip_type: TripCategory;
};

type FlightShopPassengerTrackingProperties = {
  pax_total: number;
  searched_adult_total: number;
  searched_pax_total: number;
  searched_pax_total_children: number;
  searched_pax_total_infant_lap: number;
  searched_pax_total_infant_seat: number;
};

export type FlightShopTrackingProperties =
  FlightShopSearchFormTrackingProperties &
    FlightShopPassengerTrackingProperties & {
      number_of_active_watches?: number;
      rewards_accounts?: string;
      sort_order: SortOptionEnum;
      flights_shown: number;
      fares_shown_outbound: number;
    };

const getAirportCode = (airports, airportRegion: AirportRegion): string =>
  !(airports[airportRegion.code] == null)
    ? airports[airportRegion.code].geography?.countryCode
    : airportRegion.code;

const getFlightShopSearchFormProperties = (
  searchFormValues,
  airports
): FlightShopSearchFormTrackingProperties => {
  if (
    airports == null ||
    searchFormValues == null ||
    searchFormValues.origin == null ||
    searchFormValues.origin.id.code == null ||
    searchFormValues.destination == null ||
    searchFormValues.destination.id.code == null ||
    searchFormValues.tripCategory == null ||
    searchFormValues.departureDate == null ||
    searchFormValues.returnDate == null
  ) {
    logNotFoundTrackingProperties(
      "getFlightShopSearchFormProperties",
      "flights"
    );
    return {
      advance: -1,
      advanced: -1,
      departure_date: "",
      destination: "",
      destination_country_code: "",
      los: -1,
      origin: "",
      origin_country_code: "",
      return_date: "",
      trip_type: TripCategory.ROUND_TRIP,
    };
  }

  const { origin, destination, departureDate, returnDate, tripCategory } =
    searchFormValues;

  return {
    advance: dayjs(departureDate).diff(dayjs(), "day"),
    advanced: dayjs(departureDate).diff(dayjs(), "day"),
    departure_date: dayjs(departureDate).format("YYYY-MM-DD"),
    destination: `${destination.id.code.regionType}/${destination.id.code.code}`,
    destination_country_code: getAirportCode(
      airports,
      destination.id.code.code
    ),
    los: dayjs(returnDate).diff(dayjs(departureDate), "day"),
    origin: `${origin.id.code.regionType}/${origin.id.code.code}`,
    origin_country_code: getAirportCode(airports, destination.id.code.code),
    return_date: dayjs(returnDate).format("YYYY-MM-DD"),
    trip_type: tripCategory,
  };
};

const getFlightShopPassengerTrackingProperties = (
  searchFormValues
): FlightShopPassengerTrackingProperties => {
  if (
    searchFormValues == null ||
    searchFormValues.passengerCounts == null ||
    searchFormValues.passengerCounts.adultsCount == null ||
    searchFormValues.passengerCounts.childrenCount == null ||
    searchFormValues.passengerCounts.infantsOnLapCount == null ||
    searchFormValues.passengerCounts.infantsInSeatCount == null
  ) {
    logNotFoundTrackingProperties(
      "getFlightShopPassengerTrackingProperties",
      "flights"
    );
    return {
      pax_total: 1,
      searched_pax_total: 1,
      searched_adult_total: 1,
      searched_pax_total_children: 0,
      searched_pax_total_infant_lap: 0,
      searched_pax_total_infant_seat: 0,
    };
  }

  const { passengerCounts } = searchFormValues;

  const passengerCountsTotal =
    passengerCounts.adultsCount +
    passengerCounts.childrenCount +
    passengerCounts.infantsInSeatCount +
    passengerCounts.infantsOnLapCount;

  return {
    pax_total: passengerCountsTotal,
    searched_pax_total: passengerCountsTotal,
    searched_adult_total: passengerCounts.adultsCount,
    searched_pax_total_children: passengerCounts.childrenCount,
    searched_pax_total_infant_lap: passengerCounts.infantsOnLapCount,
    searched_pax_total_infant_seat: passengerCounts.infantsInSeatCount,
  };
};

export const getFlightShopTrackingProperties = createSelector(
  getSearchFormValues,
  getAirports,
  getSortOption,
  getFlights,
  (
    searchFormValues,
    airports,
    sortOption = SortOptionEnum.FareScore,
    flights
  ): FlightShopTrackingProperties => {
    return {
      ...getFlightShopSearchFormProperties(searchFormValues, airports),
      ...getFlightShopPassengerTrackingProperties(searchFormValues),
      number_of_active_watches: 0, // TODO Fix
      rewards_accounts: undefined, // TODO Fix
      sort_order: sortOption,
      fares_shown_outbound:
        flights.data != null ? flights.data.outbound.length : 0,
      flights_shown: Object.keys(flights.data?.trips ?? {}).length ?? 0,
    };
  }
);
